<template>
  <div :class="$style.ctn">
    <Stats :class="$style.stats" :values="stats" />

    <div :class="$style.head">
      <h3 :class="[com.subtitle, $style.title]">
        Behavior graph
      </h3>
      <TipMessage mode="head">
        Click at the process to see the details
      </TipMessage>
    </div>
    <Graph :class="$style.graph" :svg="graphSvg" @select="select" />
    <AccordeonPanel
      :start-open="false"
      :head-class="$style.specsHead"
      :class="$style.specs"
      title="Specs description"
    >
      <Specs />
    </AccordeonPanel>

    <h3 :class="[com.subtitle, $style.title]">
      Process information
    </h3>
    <PaginationTable
      hash-name="processes"
      :class="$style.table"
      :head="processesHead"
      :values="processesValues"
      :pagination-detach="true"
      :table-wrapper-class="media.processTableWrapper"
    >
      <template #row="props">
        <tr
          v-show="props.show"
          :id="props.rowId"
          :class="[$style.row, $style.rowTop]"
          :style="processRowStyle(props.row.threatLevel)"
        >
          <td v-for="(col, i) in props.row.values" :key="i">
            <span :class="$style.cell">
              <template v-if="i === 0">
                <b>{{ col }}</b>
              </template>
              <template v-else-if="i === 3">
                <template v-if="col.length">
                  <span :class="$style.cellIndicators">
                    <Indicator v-for="(name, j) in col" :key="j" :name="name" />
                  </span>
                </template>
                <template v-else>
                  <span>—</span>
                </template>
              </template>
              <template v-else>{{ col }}</template>
            </span>
          </td>
        </tr>
        <tr
          v-show="props.show"
          :class="[$style.row, $style.rowBot]"
          :style="processRowStyle(props.row.threatLevel)"
        >
          <td :colspan="processesHead.length">
            <div :class="$style.cell">
              <AccordeonPanel :class="$style.accordeon" title="Information" :compact="true">
                <div :class="$style.information">
                  <div :class="$style.keyValueGrid">
                    <template v-for="(field, index) in informationFields" :key="index">
                      <template v-if="props.row.information.values[index]">
                        <div :class="com.keyValueName">
                          {{ field }}
                        </div>
                        <div :class="com.keyValueValue">
                          {{ props.row.information.values[index] }}
                        </div>
                      </template>
                    </template>
                  </div>
                  <template v-if="props.row.information.modules.length">
                    <h5 :class="$style.modulesTitle">
                      Modules
                    </h5>
                    <PaginationTable
                      :class="$style.modulesTable"
                      :pagination-detach="true"
                      :compact="true"
                      :head="modulesHead"
                      :values="props.row.information.modules"
                    />
                  </template>
                </div>
              </AccordeonPanel>

              <AccordeonPanel
                v-if="props.row.malwareConfigurations.length"
                :class="$style.accordeon"
                title="Malware configuration"
                :compact="true"
              >
                <template #head>
                  <div :class="$style.malconfHeadFamilies">
                    <div
                      v-for="familyMalConf in props.row.malwareConfigurations"
                      :class="$style.malconfHeadFamily"
                    >
                      {{ familyMalConf.family }}
                    </div>
                  </div>
                </template>

                <div :class="$style.information">
                  <template v-for="familyMalConf in props.row.malwareConfigurations">
                    <h5 :class="$style.familyName">
                      {{ familyMalConf.family }}
                    </h5>

                    <div
                      v-for="(malconf, index) in familyMalConf.malconfs"
                      :key="index"
                      :class="$style.malconfTable"
                    >
                      <TableViewerLine
                        :is-head="true"
                        name="(PID) Process"
                        :value="`(${props.row.pid}) ${props.row.fileName}`"
                      />

                      <TableViewer :data="malconf.config" />
                    </div>
                  </template>
                </div>
              </AccordeonPanel>
            </div>
          </td>
        </tr>
      </template>
    </PaginationTable>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import Graph from './Graph.vue';
import TipMessage from './TipMessage.vue';
import AccordeonPanel from './AccordeonPanel.vue';
import Specs from './Specs.vue';
import PaginationTable from './PaginationTable.vue';
import Stats from './Stats.vue';
import Indicator from './Indicator.vue';
import { CSSClassMap } from '../common/style';
import TableViewerLine from './TableViewerLine.vue';
import TableViewer from './TableViewer.vue';

export default defineComponent({
  components: {
    Graph,
    TipMessage,
    AccordeonPanel,
    Specs,
    PaginationTable,
    Stats,
    Indicator,
    TableViewerLine,
    TableViewer,
  },
  inject: ['root'],
  data() {
    return {
      processesHead: [
        { value: 'PID', width: 64 },
        { value: 'CMD', width: 340 },
        { value: 'Path', width: 340 },
        { value: 'Indicators', width: 92 },
        { value: 'Parent process', width: 244 },
      ],
      informationFields: [
        'User:',
        'Company:',
        'Integrity Level:',
        'Description:',
        'Exit code:',
        'Version:',
      ],
      modulesHead: ['Images'],
    };
  },
  computed: {
    stats() {
      return this.root.processes.stats;
    },
    graphSvg() {
      return this.root.processes.graphSvg;
    },
    processesValues() {
      return this.root.processes.processesValues;
    },
  },
  methods: {
    processRowStyle(threatLevel) {
      const substring = CSSClassMap[threatLevel];
      return {
        '--process-row-accent-color': ` var(--process-row-${substring}-color)`,
        '--process-row-accent-color-secondary': `var(--process-row-${substring}-color-secondary)`,
      };
    },
    select(id) {
      const uniquePrefix = location.hash[1] === 'i' ? 'l' : 'i';
      location.hash = `${uniquePrefix}-table-processes-${id}`;
    },
  },
});
</script>

<style lang="scss" module>
.ctn {
  padding-top: 20px;
}
.stats {
  & + .head {
    margin-top: 40px;
  }
}
.graph {
  width: 100%;
  height: 515px;

  & + .specs {
    margin-top: 15px;
  }

  @media screen and (max-width: 786px) {
    height: 215px;
  }

  @media print {
    height: 890px;
  }
}
.specs + .title {
  margin-top: 40px;
}

.specs {
  @media screen and (max-width: 877px) {
    overflow-y: hidden;
    overflow-x: auto;

    > * {
      min-width: 777px;
    }
  }
  & > .specsHead {
    padding-left: 10px;
  }
}
.head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  & + .graph {
    margin-top: 4px;
  }
}
.title + .table {
  margin-top: 15px;
}
:root {
  --process-row-info-color: #72c1eb;
  --process-row-info-color-secondary: #93daff;
  --process-row-warning-color: #f5a555;
  --process-row-warning-color-secondary: #ffce9e;
  --process-row-danger-color: #ffa4a4;
  --process-row-danger-color-secondary: #ffc5c5;
}

.rowBot > td > .cell {
  padding: 10px;
  &::before {
    border-bottom: 1px solid var(--process-row-accent-color-secondary);
  }
}

.row {
  padding-top: 6px;

  & > td {
    & > .cell {
      overflow-wrap: anywhere;
      word-break: break-word;
      display: block;
      height: 100%;
      font-size: 12px;
      color: #2f3543;
      padding-top: 12px;
      padding-left: 10px;

      &::before {
        @media print {
          --process-row-accent-color: #a5a5a5 !important;
          --process-row-accent-color-secondary: #dfdfdf !important;
        }
      }
    }
    &:first-child > .cell {
      padding-left: 16px;
      &::before {
        border-left: 4px solid var(--process-row-accent-color);
      }
    }
    &:last-child > .cell {
      padding-right: 20px;
      &::before {
        border-right: 1px solid var(--process-row-accent-color-secondary);
      }
    }
  }
}
.rowTop > td {
  padding-top: 6px;
  & > .cell::before {
    top: 6px;
    border-top: 1px solid var(--process-row-accent-color-secondary);
  }
}
.keyValueGrid {
  display: grid;
  grid-template-columns: repeat(3, auto) 1fr;
  grid-auto-flow: row;
  row-gap: 10px;
  & > *:nth-child(even) {
    padding-right: 100px;
  }
  & > *:nth-child(odd) {
    padding-right: 20px;
  }
  & + * {
    margin-top: 20px;
  }
}
.information {
  padding: 10px 20px;
}

.modulesTitle {
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.02em;
  color: #2f3543;
  & + * {
    margin-top: 4px;
  }
}

.modulesTable,
.modulesTitle {
  @media print {
    display: none;
  }
}

.cellIndicators {
  display: flex;
  flex-wrap: wrap;
  margin-top: -4px;
  margin-left: -4px;

  & > * {
    margin-left: 4px;
    margin-top: 4px;
  }
}

.table {
  --padding-left-right: 14px;
}

.accordeon {
  & + .accordeon {
    margin-top: 12px;
  }
}

.malconfHeadFamilies {
  display: flex;
  padding: 0 30px;
  margin-right: auto;
  flex: 1 1 auto;
  gap: 10px;
}
.malconfHeadFamily {
  background: #a1a4ad;
  border-radius: 2px;

  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;

  text-align: center;

  color: #ffffff;

  padding: 2px 10px;
}

.malconfTable {
  max-width: 1002px;
  margin-top: 4px;

  & + .familyName {
    margin-top: 20px;
  }
}

.familyName {
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.02em;

  color: #2f3543;
}
</style>

<style lang="scss" src="./common.module.scss" module="com"></style>
<style lang="scss" src="./media.module.scss" module="media"></style>
